import { Component } from '@theme/component';
import {
  debounce,
  onDocumentLoaded,
  setHeaderMenuStyle,
} from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const HOVER_COMMIT_DELAY_MS = 150;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuMutationObserver = null;
  #submenuObserverTimer;
  #closeTimer;
  #hoverDispatchTimer;

  #state = {
    activeItem: null,
  };

  #onMenuPointerOver = (event) => {
    if (
      event.pointerType === 'touch' ||
      !(event.target instanceof Element)
    ) {
      return;
    }

    const row = event.target.closest('.menu-list__list-item');

    if (!row || row.closest('header-menu') !== this) return;

    clearTimeout(this.#closeTimer);
    this.activate({ target: row });
  };

  #onMenuPointerOut = (event) => {
    if (event.pointerType === 'touch') return;

    const row =
      event.target instanceof Element
        ? event.target.closest('.menu-list__list-item')
        : null;

    if (
      event.relatedTarget instanceof Node &&
      row?.contains(event.relatedTarget)
    ) {
      return;
    }

    clearTimeout(this.#closeTimer);

    this.#closeTimer = setTimeout(() => {
      const activeRow = this.#state.activeItem?.parentElement;

      if (
        activeRow?.matches(':hover') ||
        activeRow?.matches(':focus-within')
      ) {
        return;
      }

      this.#deactivate();
    }, 120);
  };

  #onMenuKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    const link = this.#state.activeItem;

    link.focus();
    this.#deactivate();
    event.preventDefault();
  };

  connectedCallback() {
    super.connectedCallback();

    // Replace pointerenter/leave delegation with bubbling pointer events.
    // Keep the existing keyboard focus handlers.
    this.querySelectorAll('.menu-list__list-item').forEach((row) => {
      row.removeAttribute('on:pointerenter');
      row.removeAttribute('on:pointerleave');
    });

    this.addEventListener('pointerover', this.#onMenuPointerOver);
    this.addEventListener('pointerout', this.#onMenuPointerOut);
    this.addEventListener('keydown', this.#onMenuKeyDown);

    onDocumentLoaded(this.#preloadImages);

    window.addEventListener('resize', this.#resizeListener);

    this.overflowMenu?.addEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('pointerover', this.#onMenuPointerOver);
    this.removeEventListener('pointerout', this.#onMenuPointerOut);
    this.removeEventListener('keydown', this.#onMenuKeyDown);

    clearTimeout(this.#closeTimer);

    window.removeEventListener('resize', this.#resizeListener);

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }

    this.overflowMenu?.removeEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    this.#cleanupMutationObserver();

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  #pointerIdleTimer;

  #lastPointer = {
    x: 0,
    y: 0,
  };

  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;

    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving =
      Math.abs(event.movementX) >= 1 ||
      event.movementY >= 1;

    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);

    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;

    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);

      if (!target) return;

      const listItem = target.closest('.menu-list__list-item');

      if (
        listItem &&
        !listItem.contains(this.#state.activeItem)
      ) {
        listItem.dispatchEvent(
          new PointerEvent('pointerenter', {
            bubbles: false,
          })
        );
      }
    });
  }

  #startPointerTracking(item, previousItem) {
    // Disable the full-width invisible safety box.
    // The close delay now bridges the gap to the submenu.
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    }

    this.#stopPointerTracking(item);
  }

  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;

    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  get overflowMenu() {
    return (
      this.refs.overflowMenu?.shadowRoot?.querySelector(
        '[part="overflow"]'
      ) ?? null
    );
  }

  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(':hover') ?? false
    );
  }

  get headerComponent() {
    return this.closest('header-component');
  }

  activate = (event) => {
    if (
      !(event.target instanceof Element) ||
      !this.headerComponent
    ) {
      return;
    }

    const item = findMenuItem(event.target);

    if (!item || item === this.#state.activeItem) return;

    const isDefaultSlot =
      item.parentElement?.slot !== 'overflow';

    clearTimeout(this.#closeTimer);

    this.dataset.overflowExpanded =
      (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      const previousSubmenu = findSubmenu(previouslyActiveItem);

      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;

      const committedItem = item;

      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;

          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      submenu.dataset.active = '';

      this.#cleanupMutationObserver();

      const observedItem = item;

      const observer = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (
              this.#submenuMutationObserver !== observer ||
              this.#state.activeItem !== observedItem
            ) {
              return;
            }

            if (submenu.offsetHeight > 0) {
              this.#setFullOpenHeaderHeight(
                submenu.offsetHeight,
                this.#getHeaderVisibleHeight()
              );

              this.headerComponent?.style.setProperty(
                '--submenu-height',
                `${submenu.offsetHeight}px`
              );

              this.#cleanupMutationObserver();
            }
          });
        });
      });

      this.#submenuMutationObserver = observer;

      observer.observe(submenu, {
        childList: true,
        subtree: true,
      });

      this.#submenuObserverTimer = setTimeout(() => {
        if (this.#submenuMutationObserver === observer) {
          this.#cleanupMutationObserver();
        }
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight =
        this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight =
          this.overflowMenu?.offsetHeight || 0;

        finalHeight = Math.max(
          overflowHeight,
          overflowListHeight
        );
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    const headerVisibleHeight =
      this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty(
      '--submenu-height',
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(
      finalHeight,
      headerVisibleHeight
    );

    this.style.setProperty('--submenu-opacity', '1');

    this.#startPointerTracking(
      item,
      previouslyActiveItem
    );
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);

    const isMovingWithinMenu =
      event.relatedTarget instanceof Node &&
      this.#state.activeItem?.parentElement?.contains(
        event.relatedTarget
      );

    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      menu?.contains(event.relatedTarget);

    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node &&
      event.relatedTarget.parentElement?.matches(
        '[slot="overflow"]'
      );

    if (
      isMovingWithinMenu ||
      isMovingToOverflowMenu ||
      isMovingToSubmenu
    ) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }

      return;
    }

    this.#deactivate();
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    if (
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover')
    ) {
      return;
    }

    clearTimeout(this.#closeTimer);

    this.#cleanupMutationObserver();

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.#setFullOpenHeaderHeight(0, 0);

    this.style.setProperty('--submenu-opacity', '0');

    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    this.#stopPointerTracking(item);

    this.#state.activeItem = null;

    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu
      ?.querySelector('slot')
      ?.assignedElements();

    if (!slottedMenuLinks) {
      return this.overflowMenu?.offsetHeight || 0;
    }

    const mapSubmenus = (callback) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = link.querySelector(
          '[ref="submenu[]"]'
        );

        if (submenu) {
          callback(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });

    const height = this.overflowMenu?.offsetHeight || 0;

    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });

    return height;
  }

  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation =
      this.headerComponent.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    return isOverlapSituation &&
      this.headerComponent.offsetHeight > 0
      ? this.headerComponent.querySelector(
          '.header__row--top'
        )?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  #setFullOpenHeaderHeight(
    submenuHeight,
    headerVisibleHeight
  ) {
    if (!this.headerComponent) return;

    const fullOpenHeaderHeight =
      submenuHeight === 0
        ? 0
        : submenuHeight + headerVisibleHeight;

    this.headerComponent.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  #preloadImages = () => {
    const images = this.querySelectorAll(
      'img[loading="lazy"]'
    );

    images.forEach((image) => {
      image.removeAttribute('loading');
    });
  };

  #cleanupMutationObserver() {
    clearTimeout(this.#submenuObserverTimer);
    this.#submenuObserverTimer = undefined;

    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const row = element.closest('.menu-list__list-item');

  if (!row) return null;

  if (row.matches('[slot="more"]')) {
    return (
      row.parentElement?.querySelector(
        '[slot="overflow"] [ref="menuitem"]'
      ) ?? null
    );
  }

  return row.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector(
    '[ref="submenu[]"]'
  );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}